import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/system';
import { CompoundInterestParams } from '../../../../utils/compound-interest/types';
import { WebButton } from '../../../../components/web/button/web-button';
import { IconInfo } from '../../../../components/app/icons/info';
import { useCompoundInterestCalculator } from '../hooks/use-compound-interest-calculator';
import { CompoundInterestScenarioTabs } from '../../../../components/web/compound-interest/scenario-tabs/compound-interest-scenario-tabs/compound-interest-scenario-tabs';
import { CompoundInterestScenarioSelectMobile } from '../../../../components/web/compound-interest/scenario-tabs/compound-interest-scenario-select-mobile/compound-interest-scenario-select-mobile';
import { CompoundInterestToolbar } from '../../../../components/web/compound-interest/toolbar/compound-interest-toolbar/compound-interest-toolbar';
import { CompoundInterestForm } from '../../../../components/web/compound-interest/form/compound-interest-form/compound-interest-form';
import { CompoundInterestCalculatorProvider } from '../../../../components/web/compound-interest/calculator-context/compound-interest-calculator-context';
import { IconArrowDown } from '../../../../components/app/icons/arrow-down';
import { cn } from '../../../../utils/tailwind';
import styles from './compound-interest-calculator.module.css';

interface CompoundInterestCalculatorProps {
    params: CompoundInterestParams;
    onChange: (params: CompoundInterestParams) => void;
    isLoggedIn: boolean;
    prefilledPortfolioName?: string;
    hasPrefilledFromPortfolio?: boolean;
    canResetPrefill?: boolean;
    onResetPrefill?: () => void;
    onShowResultsClick?: () => void;
}

export const CompoundInterestCalculator: React.FC<CompoundInterestCalculatorProps> = ({
    params,
    onChange,
    isLoggedIn,
    prefilledPortfolioName,
    hasPrefilledFromPortfolio,
    canResetPrefill,
    onResetPrefill,
    onShowResultsClick,
}) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 599px)');
    const {
        activeScenario,
        allTabs,
        initialDepositSliderMax,
        isTaxExemptionByTestEnabled,
        regularDepositSliderMax,
        scenarioIcons,
        scenarioLabels,
        sliderRanges,
        handleChange,
        handleCurrencyChange,
        handleDepositFrequencyChange,
        handleNumberChange,
        handleScenarioChange,
        handleTaxExemptionByTestChange,
    } = useCompoundInterestCalculator({
        params,
        onChange,
        t,
    });

    const calculatorContextValue = {
        params,
        activeScenario,
        allTabs,
        scenarioIcons,
        scenarioLabels,
        sliderRanges,
        initialDepositSliderMax,
        regularDepositSliderMax,
        isTaxExemptionByTestEnabled,
        handleChange,
        handleNumberChange,
        handleScenarioChange,
        handleCurrencyChange,
        handleTaxExemptionByTestChange,
        handleDepositFrequencyChange,
        handleTaxRateChange: (taxRate: 0 | 15 | 23 | 35) => handleChange('taxRate', taxRate),
    };

    return (
        <div className={cn(styles.panel, 'flex flex-col gap-[3.2rem] md:gap-[4.8rem]')}>
            <CompoundInterestCalculatorProvider value={calculatorContextValue}>
                <div>
                    <div className="relative z-[1]">
                        <CompoundInterestScenarioTabs />
                        <CompoundInterestScenarioSelectMobile />
                    </div>
                    <div className={cn(styles.toolbar, 'mt-[2rem] xl:mt-[-4rem]')}>
                        <CompoundInterestToolbar />
                    </div>
                </div>

                <div className="relative">
                    <div>
                        {isLoggedIn && hasPrefilledFromPortfolio ? (
                            <div className="mb-medium flex items-start gap-[0.8rem] text-brand-primary">
                                <div className="mt-[0.15rem] shrink-0 text-brand-primary [&_i]:h-[1.8rem] [&_i]:w-[1.8rem]">
                                    <IconInfo />
                                </div>
                                <div className="flex w-full flex-1 items-start justify-between gap-[1.2rem] max-md:flex-col max-md:items-stretch max-md:gap-[0.8rem]">
                                    <span className="text-small leading-medium text-brand-primary [&_strong]:font-bold">
                                        {prefilledPortfolioName ? (
                                            <>
                                                {t('pages.compoundInterest.prefillNotePrefix')}{' '}
                                                <strong>{prefilledPortfolioName}</strong>
                                                {t('pages.compoundInterest.prefillNoteSuffix')}
                                            </>
                                        ) : (
                                            t('pages.compoundInterest.prefillNote')
                                        )}
                                    </span>

                                    {canResetPrefill && onResetPrefill ? (
                                        <div className="ml-auto flex self-start justify-end max-md:ml-0 max-md:w-full max-md:justify-center">
                                            <WebButton
                                                type="button"
                                                variant="primary"
                                                variation="outlined"
                                                tone="neutral"
                                                size={isMobile ? 'small' : 'xsmall'}
                                                block={isMobile}
                                                onClick={onResetPrefill}
                                            >
                                                {t('pages.compoundInterest.calculator.resetToPortfolio')}
                                            </WebButton>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        <div className={styles.form}>
                            <CompoundInterestForm />
                        </div>
                        <div className="mt-[4rem] flex justify-center">
                            <WebButton
                                type="button"
                                variant="primary"
                                size="vlarge"
                                iconGap={'none'}
                                icon={<IconArrowDown />}
                                onClick={onShowResultsClick}
                            >
                                {t('pages.compoundInterest.calculator.showResult')}
                            </WebButton>
                        </div>
                    </div>
                </div>
            </CompoundInterestCalculatorProvider>
        </div>
    );
};
